import {HttpClient} from '@angular/common/http';
import {MatPaginator} from '@angular/material/paginator';
import {ApiPagingQueryModel} from '@models/api/@base/api-paging-query.model';
import {ApiSuggestQueryModel} from '@models/api/@base/api-suggest-query.model';
import {Observable} from 'rxjs';
import {flatMap} from 'rxjs/operators';

export default class Utils {

    public static isJsonString(str: any): boolean {
        try {
            JSON.parse(String(str));
            return true;
        } catch (e) {
            return false;
        }
    }

    public static cleanNulls(src: any): any {
        const cpy = {};
        Object.keys(src).forEach((key) => {
            if (src[key] === null || src[key] === undefined) {
                return;
            }
            cpy[key] = src[key];
        });

        return cpy;
    }

    public static readableBytes(bytes: number): string {
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const recalc = ((bytes / Math.pow(1024, i)) * 1).toFixed(2);
        return `${recalc} ${sizes[i]}`;
    }

    public static internationalizeMatTabPaginator(matPaginator: MatPaginator): void {
        // override default pagination range label
        matPaginator._intl.itemsPerPageLabel = '';
        matPaginator._intl.firstPageLabel = '';
        matPaginator._intl.lastPageLabel = '';
        matPaginator._intl.previousPageLabel = '';
        matPaginator._intl.nextPageLabel = '';
        matPaginator._intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
            const start = page * pageSize + 1;
            let end = (page + 1) * pageSize;
            if (end > length) {
                end = length;
            }
            return start + ' - ' + end + ' / ' + length;
        };
    }

    public static niceNumberForTicks(value: number): number {

        const exponent = Math.floor(Math.log10(value)) - 1;
        const fraction = Math.round(((value * 1.05) / Math.pow(10, exponent)));
        return fraction * Math.pow(10, exponent);

    }

    public static graphCalcTicks(minValue: number, maxValue: number, meanValue: number, ticksCount: number): number[] {

        const range = Utils.niceNumberForTicks(maxValue);
        const tickValue = Utils.niceNumberForTicks(range / (ticksCount - 1));
        const ticks: number[] = [];
        for (let i = 0; i < ticksCount; i++) {
            ticks.push(minValue + tickValue * i);
        }

        return ticks;

    }

    /**
     * Generate Listing query
     * @param query
     */
    public static generatePagingQuery(query: ApiPagingQueryModel): string {
        const queries: string[] = [];
        if (query != null) {
            if (!Utils.isNullOrEmpty(query.searchTerm)) {
                queries.push(`SearchTerm=${encodeURIComponent(query.searchTerm)}`);
            }

            if (query.ordering != null) {
                if (!Utils.isNullOrEmpty(query.ordering.by)) {
                    queries.push(`Ordering.By=${encodeURIComponent(query.ordering.by)}`);
                    if (query.ordering.direction != null) {
                        queries.push(`Ordering.Direction=${encodeURIComponent(query.ordering.direction)}`);
                    }
                }
            }

            if (query.page != null) {
                queries.push(`Page.Index=${query.page.index}`);
                queries.push(`Page.Size=${query.page.size > 0 ? query.page.size : 25}`);
            }
        }

        return queries.join('&');
    }

    public static generateSuggestQuery(query: ApiSuggestQueryModel): string {
        const queries: string[] = [];
        if (query != null) {
            if (!Utils.isNullOrEmpty(query.searchTerm)) {
                queries.push(`SearchTerm=${encodeURIComponent(query.searchTerm)}`);
            }

            queries.push(`Skip=${encodeURIComponent(query.skip == null ? 0 : query.skip)}`);
            queries.push(`Take=${encodeURIComponent(query.take == null ? 0 : query.take)}`);
        }
        return queries.join('&');
    }

    /**
     * Download file as base64 stream
     * @param httpClient
     * @param url
     */
    public static downloadDataAsBase64(httpClient: HttpClient, url: string): Observable<string> {
        return httpClient.get(url, {responseType: 'blob'}).pipe(
            flatMap(blob => {
                return Utils.blobToBase64(blob);
            })
        );
    }

    /**
     * Convert blob to base64 stream
     * @param blob
     */
    public static blobToBase64(blob: Blob): Observable<any> {
        const fileReader = new FileReader();
        const observable = new Observable(observer => {
            fileReader.onloadend = () => {
                observer.next(fileReader.result);
                observer.complete();
            };
        });
        fileReader.readAsDataURL(blob);
        return observable;
    }

    /**
     * String is null or empty
     * @param val
     */
    public static isNullOrEmpty(val: string): boolean {
        return (val == null || String(val).isEmpty());
    }

    /**
     * Converts string to Number
     * @param val
     */
    public static toNumber(val: string): number {
        if (Utils.isNullOrEmpty(val)) {
            return null;
        }

        return Number(String(val).replace(/[^\d,.-]/g, ''));
    }
}
